import RoisBackgroundLocation from '../plugins/roisBackgroundLocation';
import { isNativeApp } from './positionSource';
import { permissionApiDecides } from './webPermissions';

/**
 * ¿El operario ha RECHAZADO el permiso de ubicación?
 *
 * ── Para qué se pregunta ───────────────────────────────────────────────────
 *
 * Para no dejarle abrir un tramo que no va a poder usar. El vigilante del GPS
 * (`useSurveillanceRouteGpsTracking`) ya planta un modal bloqueante cuando el
 * permiso está rechazado, pero solo se enciende con el recorrido YA en curso —
 * o sea, después de crear el tramo. El resultado era el peor orden posible: no
 * se avisaba cuando aún se podía elegir, y quedaba un tramo abierto y sin un
 * solo punto de GPS cuando ya no.
 *
 * ── 🔴 La pregunta solo se hace donde la respuesta significa algo ──────────
 *
 * En el navegador el permiso se guarda por origen, así que `permissions.query`
 * ES la decisión del usuario. Dentro del APK no: el WebView no retiene nada
 * (ver `permissionApiDecides`), y ahí quien sabe es el plugin, cuyo
 * `checkPermissions()` distingue lo que Android esconde — `'prompt'` (no se ha
 * preguntado nunca) de `'denied'` (lo rechazó a conciencia).
 *
 * ── Y por qué solo bloquea el 'denied' ────────────────────────────────────
 *
 * Porque lo contrario repetiría el error que se arregló en julio: bloquear por
 * no tener posición TODAVÍA deja al operario encerrado en el primer arranque
 * frío o en el aparcamiento de la base. Aquí no se pregunta «¿hay señal?» —
 * eso lo dirá el GPS cuando toque— sino «¿le han cerrado la puerta?».
 *
 * Ante cualquier duda (API ausente, error, `'prompt'`) devuelve `false`: no
 * bloquear nunca a quien no ha dicho que no.
 */
export async function gpsPermissionIsDenied(): Promise<boolean> {
  try {
    if (isNativeApp()) {
      const status = await RoisBackgroundLocation.checkPermissions();
      return status?.location === 'denied';
    }

    if (
      !permissionApiDecides() ||
      typeof navigator === 'undefined' ||
      !navigator.permissions?.query
    ) {
      return false;
    }

    const status = await navigator.permissions.query({
      name: 'geolocation' as PermissionName,
    });
    return status.state === 'denied';
  } catch {
    // Permissions API ausente (Safari antiguo), plugin sin responder… — ante
    // la duda se deja pasar y el flujo de siempre se encarga.
    return false;
  }
}

/**
 * ¿Arrancar el GPS del recorrido (`start()`) tendría que PEDIR algo a Android?
 *
 * `start()` pide la ubicación y las notificaciones que falten. Con las dos
 * concedidas no enseña nada y puede arrancar aunque haya algo del sistema
 * encima (ver `systemPrompts`); si falta alguna, el diálogo saldría encima de
 * ese algo. Ante la duda (plugin sin responder) contesta que sí: esperar un
 * poco es mejor que un diálogo encima de otro.
 */
export async function trackingStartWouldAsk(): Promise<boolean> {
  if (!isNativeApp()) return false;
  try {
    const status = await RoisBackgroundLocation.checkPermissions();
    return status?.location !== 'granted' || status?.notifications !== 'granted';
  } catch {
    return true;
  }
}

/**
 * ¿Está apagada la ubicación DEL SISTEMA con un permiso que sí está concedido?
 *
 * Es un caso aparte del permiso denegado y hasta ahora no lo veía nadie: en la
 * app nativa la posición la emite el servicio, no `navigator.geolocation`, así
 * que con la ubicación apagada no hay error, ni rechazo, ni nada — el servicio
 * sigue diciendo `running: true` y no llega un solo punto. Medido en tablet el
 * 2026-09-01.
 *
 * En el navegador devuelve `false`: allí sí hay `watchPosition`, que responde
 * con el código 2 y ya dispara el aviso por su cuenta.
 */
export async function systemLocationIsOff(): Promise<boolean> {
  if (!isNativeApp()) return false;
  try {
    const { enabled } = await RoisBackgroundLocation.isLocationEnabled();
    return enabled === false;
  } catch {
    // Una APK anterior a este método no lo tiene: no se bloquea por no poder
    // preguntar. Impedir trabajar sería peor que el problema que previene.
    return false;
  }
}

export default gpsPermissionIsDenied;
